<script setup lang="ts">
import { defineAsyncComponent, onMounted, onUnmounted, ref, watchEffect, type Component } from 'vue'
import { useNode } from './store'
import { virtualConfig, type EditTarget } from './api'
import { broadcastersPage, wallboard, wallPage, watchPage } from './util'
import { SHORTCUTS, shortcut } from './shortcuts'
import AppHeader from './components/AppHeader.vue'
import EntityList from './components/EntityList.vue'
import DetailPanel from './components/DetailPanel.vue'
import LoginView from './components/LoginView.vue'
import TwoFactor from './components/TwoFactor.vue'
const reloadPage = () => location.reload()
import EditDrawer from './components/EditDrawer.vue'
import GroupsBar from './components/GroupsBar.vue'
import ChangesDialog from './components/ChangesDialog.vue'
import ConfirmDialog from './components/ConfirmDialog.vue'
import StatusBar from './components/StatusBar.vue'
import LostBanner from './components/LostBanner.vue'
import Brand from './components/Brand.vue'

// the drawers and central's other pages load when opened: one chunk of
// everything (606 kB) was every venue iPad's download to see one node
// (WEB-44)
// (typed here: typescript-eslint reads no types from .vue files)
const lazy = (load: () => Promise<{ default: Component }>): Component => defineAsyncComponent(load)
const MediaDrawer = lazy(() => import('./components/MediaDrawer.vue'))
const UsersDrawer = lazy(() => import('./components/UsersDrawer.vue'))
const AuditDrawer = lazy(() => import('./components/AuditDrawer.vue'))
const TokensDrawer = lazy(() => import('./components/TokensDrawer.vue'))
const SoftwareDrawer = lazy(() => import('./components/SoftwareDrawer.vue'))
const ConfigsDrawer = lazy(() => import('./components/ConfigsDrawer.vue'))
const TemplatesDrawer = lazy(() => import('./components/TemplatesDrawer.vue'))
const AlertsDrawer = lazy(() => import('./components/AlertsDrawer.vue'))
const RouterDrawer = lazy(() => import('./components/RouterDrawer.vue'))
const NodeUpdateDrawer = lazy(() => import('./components/NodeUpdateDrawer.vue'))
const CloudDrawer = lazy(() => import('./components/CloudDrawer.vue'))
const FreezeDrawer = lazy(() => import('./components/FreezeDrawer.vue'))
const WatchView = lazy(() => import('./components/WatchView.vue'))
const WallView = lazy(() => import('./components/WallView.vue'))
const BroadcastersView = lazy(() => import('./components/BroadcastersView.vue'))
// the fleet page too (67 kB: its cards, the map of every country's flag),
// which a node's own page never shows
const FleetView = lazy(() => import('./components/FleetView.vue'))
const PairBanner = lazy(() => import('./components/PairBanner.vue')) // a paired node's only

const node = useNode()
const edit = ref<EditTarget | null>(null)
const media = ref(false)
const users = ref(false)
const changes = ref(false)
const tokens = ref(false)
const software = ref(false)
// the node's update: from the menu, or a gate's Update button (NeedsNewer)
function closeSoftware() {
    software.value = false
    node.showUpdate = false
}
const configs = ref(false)
const templates = ref(false)
const alerts = ref(false)
const router = ref(false)
// back from Google's sign-in (/?cloud=connect): the cloud drawer opens on its setup
const cloud = ref(new URLSearchParams(location.search).get('cloud') === 'connect')
const freeze = ref(false)
onMounted(() => void node.init()) // it retries by itself until the server answers

// the node page's keyboard shortcuts (shortcuts.ts, WEB-36); ? lists them
const help = ref(false)
function onKey(e: KeyboardEvent) {
    if (node.fleet || !node.config || e.defaultPrevented) return
    const t = e.target as HTMLElement | null
    const typing = !!t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)
    const modal = !!edit.value || help.value || !!document.querySelector('[role=dialog]')
    const s = shortcut(e, node.config, node.selection, typing, modal)
    if (!s) return
    e.preventDefault()
    switch (s.do) {
        case 'window':
            node.setWindow({ preset: s.preset })
            break
        case 'select':
            node.select(s.sel)
            break
        case 'edit':
            if (node.selection) edit.value = { kind: node.selection.kind, id: node.selection.id } as EditTarget
            break
        case 'help':
            help.value = true
            break
    }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
// a tab per node, told apart and showing its alarms: "(2) Rack 1 · BSEP"
watchEffect(() => {
    const alarms = node.openAlarms.length
    const name = node.fleet
        ? watchPage
            ? 'Watch'
            : wallPage
              ? 'Wall'
              : broadcastersPage
                ? 'Broadcasters'
                : 'Fleet'
        : node.config?.node.name || 'Node'
    document.title = `${alarms ? `(${alarms}) ` : ''}${name} · BSEP`
})
</script>

<template>
    <LoginView v-if="node.authChecked && !node.loggedIn" />
    <!-- a superadmin without two-factor: set it up before anything else (central totp.go) -->
    <div v-else-if="node.central && node.user?.totp_enrol" class="flex h-full items-center justify-center">
        <div class="w-110 max-w-full rounded-[10px] border border-line bg-bg-1 px-6 py-5.5" data-test="totp-enrol">
            <Brand sub="Central" />
            <h1 class="mt-1.5 mb-3 text-[17px] font-medium">Set up two-factor sign-in</h1>
            <p class="mt-0 text-fg-2">
                Superadmins sign in with a code from an authenticator app as well as the password. Set it up to go on.
            </p>
            <!-- set up: the whole app, loaded afresh -->
            <TwoFactor :user="node.user" @done="reloadPage" />
            <button type="button" class="mt-3 btn-ghost text-xs" @click="node.logout()">Sign out</button>
        </div>
    </div>
    <!-- central's watch page: one source across the fleet, large -->
    <WatchView v-else-if="node.fleet && watchPage" />
    <!-- central's monitor wall: saved walls of tiles across the fleet -->
    <WallView v-else-if="node.fleet && wallPage" />
    <!-- central's broadcaster directory -->
    <BroadcastersView v-else-if="node.fleet && broadcastersPage" />
    <!-- central without a node in the URL: the fleet page -->
    <div v-else-if="node.fleet" class="flex h-full flex-col max-[900px]:h-auto">
        <AppHeader
            v-if="!wallboard"
            @media="media = true"
            @users="users = true"
            @changes="changes = true"
            @tokens="tokens = true"
            @software="software = true"
            @configs="configs = true"
            @templates="templates = true"
            @alerts="alerts = true"
            @cloud="cloud = true"
            @freeze="freeze = true"
        />
        <main
            class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] gap-3 p-3 max-[900px]:block"
        >
            <FleetView class="min-h-0 overflow-auto max-[900px]:overflow-visible" @cloud="cloud = true" />
        </main>
        <MediaDrawer v-if="media" @close="media = false" />
        <UsersDrawer v-if="users" @close="users = false" />
        <AuditDrawer v-if="changes" @close="changes = false" />
        <TokensDrawer v-if="tokens" @close="tokens = false" />
        <SoftwareDrawer v-if="software" @close="software = false" />
        <ConfigsDrawer v-if="configs" @close="configs = false" />
        <TemplatesDrawer v-if="templates" @close="templates = false" />
        <AlertsDrawer v-if="alerts" @close="alerts = false" />
        <CloudDrawer v-if="cloud" @close="cloud = false" />
        <FreezeDrawer v-if="freeze" @close="freeze = false" />
        <StatusBar />
    </div>
    <div v-else-if="node.config && node.status" class="flex h-full flex-col max-[900px]:h-auto">
        <AppHeader
            @edit-node="edit = { kind: 'node' }"
            @media="media = true"
            @router="router = true"
            @software="software = true"
        />
        <div v-if="virtualConfig !== null" class="border-b border-line bg-accent/10 px-3 py-2 text-xs">
            <b>Virtual node</b> · the saved config “{{ node.config.node.name }}”, on the hardware set for it. Nothing
            runs here; every change is saved to the config. Deploy it from <a href="/">Configs on the fleet page</a>.
        </div>
        <LostBanner v-if="node.lostAt" />
        <PairBanner v-if="node.pair && virtualConfig === null" />
        <GroupsBar v-if="node.config.mode !== 'player'" @edit="(id) => (edit = { kind: 'group', id })" />
        <main
            class="relative grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-3 p-3 max-[900px]:flex max-[900px]:flex-col"
            :class="node.panelOpen ? 'grid-cols-[minmax(0,1fr)_420px]' : 'grid-cols-[minmax(0,1fr)]'"
        >
            <EntityList class="min-h-0 overflow-auto max-[900px]:overflow-visible" @add="(t) => (edit = t)" />
            <!-- the panel's column stays while it is open, a selection or not: picking
                 something fills it instead of squeezing the list (on a phone it waits for one);
                 its ✕ and the menu hide it -->
            <DetailPanel
                v-if="node.panelOpen"
                class="min-h-0 overflow-auto max-[900px]:overflow-visible"
                :class="{ 'max-[900px]:hidden': !node.selection }"
                @edit="(t) => (edit = t)"
            />
            <!-- lost its node (LostBanner): greyed, so no dot or meter reads as live; a
                 layer over it, not a filter on it, which would move its fixed dialogs -->
            <div
                v-if="node.lostAt"
                class="pointer-events-none absolute inset-0 z-20 bg-bg/35 backdrop-saturate-[.15]"
                data-testid="lost-veil"
            />
        </main>
        <EditDrawer v-if="edit" :target="edit" @close="edit = null" />
        <MediaDrawer v-if="media" @close="media = false" />
        <RouterDrawer v-if="router" @close="router = false" />
        <NodeUpdateDrawer v-if="software || node.showUpdate" @close="closeSoftware" />
        <ChangesDialog />
        <ConfirmDialog
            v-if="help"
            title="Keyboard"
            confirm-label="Close"
            @confirm="help = false"
            @cancel="help = false"
        >
            <table class="mb-3 text-xs" data-testid="shortcuts">
                <tr v-for="[k, what] in SHORTCUTS" :key="k">
                    <td class="pr-3 font-mono whitespace-nowrap">{{ k }}</td>
                    <td class="text-fg-2">{{ what }}</td>
                </tr>
            </table>
        </ConfirmDialog>
        <StatusBar />
    </div>
    <div v-else class="flex h-full flex-col items-center justify-center gap-2 text-fg-2">
        <template v-if="node.connectError">
            <div class="text-bad">{{ node.connectError }}</div>
            <div class="text-xs text-fg-3">
                Trying again every few seconds: the page opens by itself when it answers.
            </div>
            <a v-if="node.central" href="/" class="text-xs">← back to the fleet</a>
        </template>
        <template v-else>Connecting…</template>
    </div>
</template>
